import { useState, useRef, useEffect } from "react";
import axios from "axios";  // MAGIC-1
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, ScanFace } from "lucide-react";
import { isPlatformPasskeyAvailable } from "@/lib/passkeys";
import { useDemoInfo } from "@/lib/demo";  // DEMO-1d
import StaffSecondStep from "@/components/auth/StaffSecondStep";  // STAFF-MFA-1

// The real white logo file: the navy-text versions disappear on the navy band.
const LOGO_WHITE = "/logos/solara-horizontal-white.png";
const FIELD =
  "h-[52px] mt-1.5 rounded-xl border-line bg-white px-4 text-[15px] md:text-[15px] text-navy shadow-none placeholder:text-ink-muted/70";
const TEXT_LINK =
  "min-h-11 inline-flex items-center font-semibold text-gold-ink underline underline-offset-2 hover:text-navy";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, loginWithPasskey, loginWithDemo } = useAuth();
  const demo = useDemoInfo();  // DEMO-1d — null everywhere but the demo site
  const [demoBusy, setDemoBusy] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [magicBusy, setMagicBusy] = useState(false);  // MAGIC-1
  const [passkeyBusy, setPasskeyBusy] = useState(false);  // MOB-16 / C3-b
  const [passkeySupported, setPasskeySupported] = useState(false);
  // STAFF-MFA-1 — set after a correct staff password: show the second step.
  const [challenge, setChallenge] = useState(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  // MOB-16 / C3-b — only show the Face ID button when the device actually has
  // a platform authenticator. Passkeys are staff-only; the button is harmless
  // for portal users (verify just fails generically) but we hide it unless the
  // capability is present so we don't confuse them.
  useEffect(() => {
    let alive = true;
    isPlatformPasskeyAvailable().then((ok) => { if (alive) setPasskeySupported(ok); });
    return () => { alive = false; };
  }, []);

  const routeAfterLogin = (user) => {
    if (user.must_change_password) navigate("/change-password");
    // HF-Team-Vis-1 (2026-07-29) — junior_broker is staff too; without this
    // a junior_broker logging in was sent to /my-applications like a client.
    else if (user.role === "admin" || user.role === "super_admin" || user.role === "broker" || user.role === "junior_broker") navigate("/admin");
    else if (user.role === "introducer") navigate("/introducer");
    else if (user.role === "partner") navigate("/partner/dashboard");
    else navigate("/my-applications");
  };

  // MOB-16 / C3-b + C3-c — passkey sign-in. Must run inside this click handler
  // (user gesture) for iOS. Email is OPTIONAL now: with discoverable passkeys
  // the authenticator identifies the account, so no email is required. If the
  // user did type one, we pass it through to scope the credential list.
  const handlePasskey = async () => {
    const submitEmail = (emailRef.current?.value || email).trim();
    setPasskeyBusy(true);
    try {
      const user = await loginWithPasskey(submitEmail);
      toast.success(`Welcome back, ${user.name}!`);
      routeAfterLogin(user);
    } catch (error) {
      // Swallow the user-cancelled case quietly; surface real failures.
      const name = error?.name || "";
      if (name === "NotAllowedError" || name === "AbortError") {
        // user dismissed the prompt — no toast
      } else {
        toast.error(error.response?.data?.detail || "Passkey sign-in didn't work — try your password.");
      }
    } finally {
      setPasskeyBusy(false);
    }
  };

  // DEMO-1d — one-click sign-in as a fictional demo user.
  const handleDemo = async (role) => {
    setDemoBusy(role);
    try {
      const user = await loginWithDemo(role);
      toast.success(`Signed in to the demo as ${user.name}`);
      routeAfterLogin(user);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't open the demo. Try again in a minute.");
    } finally {
      setDemoBusy("");
    }
  };

  // MAGIC-1 — request a one-time emailed sign-in link. Response is always
  // generic (no account enumeration); portal roles only, 15-minute expiry.
  const handleMagicLink = async () => {
    const submitEmail = (emailRef.current?.value || email).trim();
    if (!submitEmail) {
      toast.error("Enter your email above first, then request a sign-in link");
      emailRef.current?.focus();
      return;
    }
    setMagicBusy(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/magic-link/request`, { email: submitEmail });
      toast.success("If that email has an account, a sign-in link is on its way — it lasts 15 minutes.", { duration: 8000 });
    } catch (err) {
      toast.error("Couldn't request a sign-in link — please try again.");
    } finally {
      setMagicBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Always read from DOM refs — handles password manager autofill
    const submitEmail = (emailRef.current?.value || email).trim();
    const submitPassword = passwordRef.current?.value || password;

    if (!submitEmail || !submitPassword) {
      toast.error("Please enter your email and password");
      setIsLoading(false);
      return;
    }

    try {
      const user = await login(submitEmail, submitPassword);
      if (user?.mfaRequired) {
        setPassword("");
        if (passwordRef.current) passwordRef.current.value = "";
        setChallenge(user);
        return;
      }
      toast.success(`Welcome back, ${user.name}!`);
      // PR #100 / AUM-9 — forced-pw-change gate handled inside routeAfterLogin.
      routeAfterLogin(user);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid email or password");
    } finally {
      setIsLoading(false);
    }
  };

  // SAF-37 / SCRM-21 — the approved sign-in (Mark, 1 Nov 2026): white page, a navy
  // band carrying the real white logo (top on a phone, the left half on a desktop),
  // Playfair heading with a short gold rule, 52px fields and pill buttons, links in
  // gold-ink (#7a5c28, 6.2:1 on white) and every tap target at least 44px.
  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-white text-navy">
      <section className="flex-none h-[220px] md:h-auto md:flex-1 bg-navy flex items-center justify-center px-6 md:p-12">
        <Link to="/" className="block w-[220px] md:w-[min(360px,80%)]" aria-label="Solara Global Capital Partners, home">
          <img src={LOGO_WHITE} alt="Solara Global Capital Partners" className="w-full h-auto" />
        </Link>
      </section>

      {/* !pb: index.css gives every <main> 6rem bottom padding on phones. */}
      <main className="flex-1 flex flex-col px-6 py-8 !pb-8 md:items-center md:justify-center md:p-12 md:!pb-12">
        <div className="w-full md:max-w-[400px] flex-1 md:flex-none flex flex-col">
            {challenge ? (
              <StaffSecondStep
                challenge={challenge}
                onCancel={() => setChallenge(null)}
                onSignedIn={(u) => {
                  setChallenge(null);
                  toast.success(`Welcome back, ${u.name}!`);
                  routeAfterLogin(u);
                }}
              />
            ) : (<>
            <h1 className="text-[32px] leading-[38px] md:text-[40px] md:leading-[46px] font-semibold text-navy m-0">
              Welcome back
            </h1>
            <div className="h-px w-14 bg-gold mt-[18px] mb-6 md:my-6" aria-hidden="true" />

            {/* DEMO-1d — only on the demo site. */}
            {demo && (
              <div className="mb-6 rounded-xl border border-gold/40 bg-gold/10 p-4" data-testid="demo-logins">
                <p className="text-sm font-semibold text-navy mb-1">Try the demo</p>
                <p className="text-[13px] text-ink-muted mb-3">
                  Every client and deal here is made up. Change anything you like: nothing is sent, and it all resets overnight.
                </p>
                <div className="space-y-2">
                  {demo.logins.map((l) => (
                    <button
                      key={l.key}
                      type="button"
                      onClick={() => handleDemo(l.key)}
                      disabled={!!demoBusy}
                      className="w-full min-h-11 text-left bg-white border border-line hover:border-navy rounded-xl px-3 py-2 transition-colors disabled:opacity-50"
                      data-testid={`demo-login-${l.key}`}
                    >
                      <span className="block text-sm font-medium text-navy">
                        {demoBusy === l.key ? "Opening…" : `Sign in as ${l.label}`}
                      </span>
                      <span className="block text-[13px] text-ink-muted">{l.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <Label htmlFor="login-email" className="block text-[13px] font-semibold text-ink-muted">Email</Label>
              <Input
                id="login-email"
                ref={emailRef}
                type="email"
                name="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className={FIELD}
                data-testid="login-email"
              />

              <Label htmlFor="login-password" className="block mt-3.5 text-[13px] font-semibold text-ink-muted">Password</Label>
              <div className="relative">
                <Input
                  id="login-password"
                  ref={passwordRef}
                  type={showPassword ? "text" : "password"}
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  className={`${FIELD} pr-12`}
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-1 top-1/2 -translate-y-1/2 mt-[3px] w-11 h-11 flex items-center justify-center rounded-full text-ink-muted hover:text-navy"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div className="flex justify-end">
                <Link to="/forgot-password" className={`${TEXT_LINK} text-[13px]`} data-testid="forgot-password-link">
                  Forgot your password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full h-[52px] mt-2 rounded-full bg-navy hover:bg-navy-light text-white text-base font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                data-testid="login-submit"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  "Sign in"
                )}
              </button>
              <p className="mt-3 mb-0 text-center text-[13px] text-ink-muted">
                Staff: next, the code from your authenticator app.
              </p>

              {/* MOB-16 / C3-b — passkey (Face ID / Touch ID) sign-in. Staff
                  only server-side; shown only when a platform authenticator is
                  present. type=button so it never submits the password form. */}
              {passkeySupported && (
                <button
                  type="button"
                  onClick={handlePasskey}
                  disabled={passkeyBusy}
                  className="w-full h-[52px] mt-4 rounded-full border border-navy bg-white text-navy hover:bg-navy/5 text-base font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                  data-testid="login-passkey"
                >
                  {passkeyBusy ? (
                    <div className="w-5 h-5 border-2 border-navy/30 border-t-navy rounded-full animate-spin" />
                  ) : (
                    <>
                      <ScanFace className="w-5 h-5" />
                      Sign in with Face ID
                    </>
                  )}
                </button>
              )}
            </form>

            {/* MAGIC-1 — passwordless sign-in for portal users (uses the email
                field above), and Register, at the foot of the screen. */}
            <div className="flex justify-between gap-4 mt-auto pt-6 md:mt-8 md:pt-0">
              <button
                type="button"
                onClick={handleMagicLink}
                disabled={magicBusy}
                className={`${TEXT_LINK} text-sm disabled:opacity-50`}
                data-testid="magic-link-btn"
              >
                {magicBusy ? "Sending sign-in link…" : "Email me a sign-in link"}
              </button>
              <Link to="/register" className={`${TEXT_LINK} text-sm`} data-testid="register-link">
                Register
              </Link>
            </div>
            </>)}
        </div>
      </main>
    </div>
  );
}
